/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-statements */
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { Card, Icon, Title } from '@nimbus-ds/components';
import { DownloadIcon, PrinterIcon } from '@nimbus-ds/icons';
import {
  FulfillmentOrderAssignedLocationDto,
  GetDocumentPageSizeSettingResponseDto,
  GetDocumentSettingsResponseDto,
  InformationForContentDeclarationResponseDto,
  InformationForDocumentResponseDto,
} from '@tiendanube/common';
import { useNavegate } from 'App/hooks';
import {
  CancelAndConfirmButtons,
  Stack,
  useResponsive,
} from 'commons/components';
import {
  trackingShippingPrintDocumentCancelButtonClick,
  trackingShippingPrintDocumentSettings,
} from 'domains/FulfillmentOrders/tracking';
import useTranslationFulfillmentOrders from 'domains/FulfillmentOrders/useTranslationFulfillmentOrders';
import { DocumentType } from '../../constants';
import useDocumentSettings from '../../hooks/useDocumentsSettings';
import {
  SHOW_PRODUCTS_INFO_LABEL,
  LABEL_SETTING_SUB_OPTIONS,
} from '../../LabelSettings';
import {
  getAssignedLocationsByInformation,
  getOrderNumberAndFulfillmentOrderNumber,
  validateSizes,
} from '../../utils';
import DocumentLocationForm from '../DocumentLocationForm';
import DocumentPageSettingsForm from '../DocumentPageSettingsForm';
import DocumentSettingsForm from '../DocumentSettingsForm';
import FulfillmentOrderSelectedHeader from '../FulfillmentOrderSelectedHeader';

export interface PageSizesValidation {
  width: boolean;
  height: boolean;
}

interface PrintDocumentFormProps {
  type: DocumentType;
  settings: GetDocumentSettingsResponseDto;
  isPrinting: boolean;
  locationId?: string;
  information?:
    | InformationForContentDeclarationResponseDto[]
    | InformationForDocumentResponseDto[];
  hasOca: boolean;
  setSettings: Dispatch<SetStateAction<GetDocumentSettingsResponseDto>>;
  setLocationId: Dispatch<SetStateAction<string>>;
  handleDownload: () => void;
}

function PrintDocumentForm({
  type,
  settings,
  isPrinting,
  locationId,
  information,
  hasOca,
  setSettings,
  setLocationId,
  handleDownload,
}: Readonly<PrintDocumentFormProps>) {
  const t = useTranslationFulfillmentOrders('printManager');
  const { goTo } = useNavegate();
  const { isMobile } = useResponsive();
  const { isLoading, documentSettings, updateSetting } = useDocumentSettings();
  const [hasIncorrectSizes, setHasIncorrectSizes] =
    useState<PageSizesValidation>({
      width: false,
      height: false,
    });

  const handleSubOptionsForMainSetting = (
    mainSettingName: string,
    isChecked: boolean,
  ) => {
    if (mainSettingName === SHOW_PRODUCTS_INFO_LABEL && isChecked) {
      LABEL_SETTING_SUB_OPTIONS.forEach((subOption) => {
        setSettings((prevSettings) => ({
          ...prevSettings,
          [subOption.name]: true,
        }));
        updateSetting({ [subOption.name]: true });
        trackingShippingPrintDocumentSettings(type, subOption.name, true);
      });
    }
  };

  const onChangeSetting = (name: string, value: boolean) => {
    setSettings({
      ...settings,
      [name]: value,
    });
    updateSetting({ [name]: value });
    trackingShippingPrintDocumentSettings(type, name, value);
    handleSubOptionsForMainSetting(name, value);
  };

  const onChangeNumberSetting = (name: string, value: number) => {
    setSettings({
      ...settings,
      [name]: value,
    });
    updateSetting({ [name]: value });
    trackingShippingPrintDocumentSettings(type, name, value);
  };

  const onChangePageSize = (
    name: string,
    value: GetDocumentPageSizeSettingResponseDto,
  ) => {
    setSettings({
      ...settings,
      pageSize: value,
    });

    const validatedSizes = validateSizes(value);
    setHasIncorrectSizes(validatedSizes);
    if (!validatedSizes.width && !validatedSizes.height) {
      updateSetting({ [name]: value });
    }
  };

  const allAssignedLocations = information
    ? getAssignedLocationsByInformation(information)
    : [];

  const assignedLocationsWithoutDuplicates: FulfillmentOrderAssignedLocationDto[] =
    [
      ...new Map(
        allAssignedLocations.map((location) => [location.id, location]),
      ).values(),
    ];

  const orderAndFulfillmentOrderNumbers =
    getOrderNumberAndFulfillmentOrderNumber(information ?? []);

  const handleSelectLocation = (e: ChangeEvent<HTMLSelectElement>) => {
    setLocationId(e.target.value);
  };

  const isLoadingInformation = !information || information.length === 0;

  const hasPickupType =
    information?.some(
      (info) => info.fulfillmentOrder?.shipping?.type === 'pickup',
    ) || false;

  const handleCancel = () => {
    trackingShippingPrintDocumentCancelButtonClick(type, 'footer');
    goTo('/orders');
  };

  useEffect(() => {
    if (documentSettings) {
      setSettings(documentSettings);
    }
  }, [documentSettings, setSettings]);

  return (
    <Card>
      <Card.Header>
        <Title as="h3">{t('formTitle')}</Title>
      </Card.Header>
      <Card.Body>
        <Stack column align="stretch" spacing="loose">
          <FulfillmentOrderSelectedHeader
            labels={orderAndFulfillmentOrderNumbers}
            isLoading={!information}
          />
          {assignedLocationsWithoutDuplicates.length > 1 && (
            <DocumentLocationForm
              locationId={locationId}
              type={type}
              locations={assignedLocationsWithoutDuplicates}
              handleSelectLocation={handleSelectLocation}
            />
          )}
          {type !== DocumentType.CONTENT_DECLARATION && (
            <>
              <hr />
              <DocumentSettingsForm
                settings={settings}
                isLoading={isLoading && !information}
                onChangeSetting={onChangeSetting}
                hasPickup={hasPickupType}
                type={type}
                hasOca={hasOca}
              />
              {!isLoading && information && !hasOca && (
                <DocumentPageSettingsForm
                  type={type}
                  settings={settings}
                  onChangePageSize={onChangePageSize}
                  hasIncorrectSizes={hasIncorrectSizes}
                  onChangeSetting={onChangeSetting}
                  onChangeNumberSetting={onChangeNumberSetting}
                />
              )}
            </>
          )}
        </Stack>
      </Card.Body>
      <Card.Footer>
        <CancelAndConfirmButtons
          onCancel={handleCancel}
          onConfirm={handleDownload}
          confirmText={
            <>
              <Icon
                color="currentColor"
                source={
                  (settings.downloadDocument || isMobile) && !hasOca ? (
                    <DownloadIcon size="small" />
                  ) : (
                    <PrinterIcon size="small" />
                  )
                }
              />
              {(settings.downloadDocument || isMobile) && !hasOca
                ? t('download')
                : t('print')}
            </>
          }
          isLoading={isLoading || isPrinting || isLoadingInformation}
          isConfirmDisabled={
            hasIncorrectSizes.width || hasIncorrectSizes.height
          }
        />
      </Card.Footer>
    </Card>
  );
}

export default PrintDocumentForm;
